import type {
  ParticipantEmbeddableForm,
  UpdateParticipantEmbeddableFormRequest,
} from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z, zodHttpUrl, zodNonEmptyString } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { useUpdateParticipantEmbeddableFormMutation } from '../../graphql/hooks/useUpdateParticipantEmbeddableFormMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { ParticipantEmbeddableFormForm } from './ParticipantEmbeddableFormForm';

type FormValues = UpdateParticipantEmbeddableFormRequest;

const schema: z.ZodSchema<UpdateParticipantEmbeddableFormRequest> = z.object({
  name: zodNonEmptyString('No name found'),
  url: zodHttpUrl('Invalid URL, must start with https or http'),
});

interface Props {
  form: ParticipantEmbeddableForm;
  participantId: string;
}

export const ParticipantEmbeddableFormEditModal = createModal<Props>((props) => {
  const { form, participantId, modal } = props;

  const { hide, visible, remove } = modal;
  const { organization, successToast, errorToast } = useAppContext();

  const mutation = useUpdateParticipantEmbeddableFormMutation();

  const initialValues: FormValues = {
    name: form.name,
    url: form.url,
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          participantId: participantId,
          participantEmbeddableFormId: form.id,
          request: values,
        })
        .then(async () => {
          hide();
          successToast('Embeddable form updated successfully');
        })
        .catch((err) => {
          errorToast(`Error updating embeddable form: ${err.message}`);
        });
    },
    [mutation, organization.id, participantId, form.id, hide, successToast, errorToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Edit embeddable form'
                subtitle='Change embeddable form details'
              />
              <ChakraV3.Dialog.Body>
                <ParticipantEmbeddableFormForm />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
